import type { ReactNode } from 'react';
import { Cascader, Form, Input, Radio, Select } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react';
import { parseTicketFieldOptions, type TicketField, type VocabTreeNode } from '../api/ticket';

interface DynamicTicketFieldsProps {
  /** 父级表单实例（字段绑定到 df_<field_key>） */
  form: FormInstance;
  /** 字段定义（坐席/创建传启用字段；admin 编辑可传启用+有历史值的停用字段） */
  fields: TicketField[];
  /** 级联分类树（cascade 类型字段渲染用） */
  categoryTree: VocabTreeNode[];
}

/** 分类树 → arco Cascader 选项 */
function toCascade(nodes: VocabTreeNode[]): any[] {
  return nodes.map((n) => ({
    label: n.name,
    value: n.code,
    children: n.children?.length ? toCascade(n.children) : undefined,
  }));
}

/**
 * 工单「扩展信息」动态字段区（v1.16）：
 * 工单管理页与坐席工作台共用的统一渲染组件。按字段 type 渲染控件，值均绑定到
 * form 的 `df_<field_key>`；必填项强制校验，非空值在提交时由父组件收集为 dynamicFields。
 */
export default function DynamicTicketFields({ form, fields, categoryTree }: DynamicTicketFieldsProps) {
  if (!fields.length) return null;
  return (
    <>
      <div className="wb-form-section-title">扩展信息</div>
      <div className="wb-form-grid2">
        {fields.map((f) => {
          const fieldProps: any = { placeholder: f.placeholder || `请输入${f.label}` };
          let control: ReactNode;
          if (f.type === 'select') {
            control = <Select options={parseTicketFieldOptions(f.options)} />;
          } else if (f.type === 'radio') {
            control = <Radio.Group options={parseTicketFieldOptions(f.options)} />;
          } else if (f.type === 'cascade') {
            control = <Cascader options={toCascade(categoryTree)} placeholder="选择分类（大类 → 中类 → 小类）" />;
          } else if (f.type === 'textarea') {
            control = <Input.TextArea rows={2} {...fieldProps} />;
          } else {
            control = <Input {...fieldProps} />;
          }
          return (
            <Form.Item
              key={f.id ?? f.field_key}
              field={`df_${f.field_key}`}
              label={f.label}
              rules={f.required ? [{ required: true, message: `请填写${f.label}` }] : undefined}
            >
              {control}
            </Form.Item>
          );
        })}
      </div>
    </>
  );
}
